//Built-in libraries

import { MapPinIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useSelector } from 'react-redux';
//Third party libraries
import { useLocation, useNavigate } from 'react-router';

//Custom components
import SectionHeading from '@/components/SectionHeading';
import ProjectsListControls from '@/components/Sherpa/Projects/ProjectsListControls';
//Services
import { useProjectsData } from '../../../../hooks/ReactQueryHooks/useProjectQuery';
import { workspaceSelector } from '../../../../store';
//Assets andd Miscellaneous
import type { FiltersInterface } from '../../../../types/Miscellaneous';
import { isObjectEmpty } from '../../../../utils';
import { CardViewProjectsList } from './CardViewProjectsList';
import { MapViewProjectsList } from './MapViewProjectsList';
import { TableViewProjectsList } from './TableViewProjectsList';

//Component Begin
export const ProjectsList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [projectView, setProjectView] = useState('card');
  const [appliedFilters, setAppliedFilters] = useState<FiltersInterface>({});
  const fetchProjects = useProjectsData({});
  const { currentWorkspace } = useSelector(workspaceSelector);

  const hasProjectQuota = fetchProjects?.data?.meta?.maxProjectAllowed > 0;
  const projectsLeft = fetchProjects?.data?.meta?.maxProjectAllowed - fetchProjects?.data?.meta?.total;

  return (
    <div className='container mx-auto'>
      <div className='-mr-5'>
        <SectionHeading
          title='Projects'
          buttonConfig={{
            label: 'New Project',
            onClick: () => navigate(`${location.pathname}/new`),
            permission: {
              of: 'CREATE_PROJECT',
            },
          }}
        />
      </div>

      {hasProjectQuota && (
        <div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 mb-5'>
          You can create{' '}
          <span className='font-semibold'>
            {projectsLeft} more project{projectsLeft === 1 ? '' : 's'}
          </span>{' '}
          with your current plan.
        </div>
      )}

      <ProjectsListControls
        appliedFilters={appliedFilters}
        projectView={projectView}
        projectViewChangeHandler={(view: string) => setProjectView(view)}
        handleFiltersChange={(filters) => setAppliedFilters(filters)}
      />

      {projectView === 'card' && <CardViewProjectsList filters={appliedFilters} />}
      {projectView === 'list' && <TableViewProjectsList filters={appliedFilters} />}
      {projectView === 'map' && <MapViewProjectsList filters={appliedFilters} />}
      {fetchProjects?.data?.meta?.total === 0 && isObjectEmpty(appliedFilters) && (
        <section className='w-full flex justify-center items-center h-screen/2 bg-white'>
          <div className='max-w-lg text-center'>
            <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
              <MapPinIcon className='h-8 w-8' />
              <h1>No Projects</h1>
            </div>
            <p>
              There are no projects to display in{' '}
              {currentWorkspace ? <span className='text-primaryColor-normal'>{currentWorkspace?.name}</span> : 'all'} workspace.
            </p>
          </div>
        </section>
      )}
    </div>
  );
};
